import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { calendarViewScope } from "@/lib/permissions";
import { toast } from "sonner";
import Logo from "@/components/layout/Logo";
import { Mail, ArrowRight, ArrowLeft, KeyRound, RefreshCw, Loader2 } from "lucide-react";

/**
 * Employee sign-in — EMAIL ONE-TIME CODE ONLY (Jun 2026).
 *
 * The username + password form was removed entirely. The employee types
 * their work email, we email a 6-digit code, they type it in. The
 * session then stays active until they sign out or the same account
 * signs in somewhere else.
 */
const RESEND_COOLDOWN_SEC = 60;

export default function StaffLogin() {
  const { requestStaffOtp, resendStaffOtp, verifyStaffOtp } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [pendingId, setPendingId] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const emailRef = useRef(null);
  const autoSubmittedRef = useRef("");

  const trimmedEmail = useMemo(() => email.trim().toLowerCase(), [email]);
  const emailValid = useMemo(
    () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail),
    [trimmedEmail]
  );
  const codeValid = code.trim().length === 6;

  useEffect(() => {
    if (step === "email" && emailRef.current) emailRef.current.focus();
  }, [step]);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setInterval(() => setResendIn((n) => (n <= 1 ? 0 : n - 1)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const announce = (r) => {
    setResendIn(RESEND_COOLDOWN_SEC);
    if (r.dev_otp) toast.info(`Dev mode — code: ${r.dev_otp}`, { duration: 8000 });
    else if (r.delivered === false) {
      // The profile was found but the send itself failed — say so instead of
      // leaving the employee staring at an empty inbox.
      toast.error(
        `We couldn't send the code to ${r.email || "your email"}. Please tell an administrator — the address may be rejecting our mail.`,
        { duration: 9000 }
      );
    } else toast.success("Sign-in code sent to your email");
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!emailValid) { toast.error("Please enter a valid work email address"); return; }
    setBusy(true);
    try {
      const r = await requestStaffOtp(trimmedEmail);
      setPendingId(r.pending_id);
      announce(r);
      setStep("code");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!codeValid) { toast.error("Enter the 6-digit code from your email"); return; }
    setBusy(true);
    try {
      const { user, previousSessionReplaced } = await verifyStaffOtp(pendingId, code.trim());
      toast.success(`Welcome, ${user.full_name}`);
      if (previousSessionReplaced) {
        toast.info("Active session on previous device has been closed.", { duration: 4500 });
      }
      const scope = calendarViewScope(user);
      if (!user.is_owner && scope.personal && !scope.global) nav("/staff/my-roster");
      else nav("/staff/branches");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  // Auto-submit once all 6 digits are entered (the Sign in button stays available).
  useEffect(() => {
    if (step !== "code" || !codeValid || busy || !pendingId) return;
    if (autoSubmittedRef.current === code.trim()) return;
    autoSubmittedRef.current = code.trim();
    submitCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, codeValid, busy, pendingId, step]);

  const resend = async () => {
    if (busy || resendIn > 0 || !pendingId) return;
    setBusy(true);
    try {
      const r = await resendStaffOtp(pendingId);
      setPendingId(r.pending_id);
      setCode("");
      autoSubmittedRef.current = "";
      announce(r);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5">
      <div className="lg:col-span-2 flex items-center justify-center p-8 lg:p-14 bg-white border-r border-border">
        <div className="w-full max-w-sm" data-testid="staff-login-form">
          <Logo variant="mark" to="/" size={56} />
          <p className="tracking-overline text-[11px] text-muted-foreground mt-8">INTERNAL WORKSPACE</p>

          {step === "email" ? (
            <form onSubmit={submitEmail} data-testid="staff-login-form-email">
              <h1 className="text-2xl font-semibold mt-2">Staff sign in</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Enter your work email and we'll send a one-time code. No passwords.
              </p>
              <div className="mt-8 space-y-4">
                <div>
                  <Label htmlFor="staff-email">Work email</Label>
                  <div className="relative mt-1">
                    <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                    <Input
                      id="staff-email"
                      ref={emailRef}
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@cocoonmedicalspa.com"
                      className="pl-9"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      autoComplete="email"
                      required
                      data-testid="staff-login-email"
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={busy || !emailValid} data-testid="staff-login-send-code">
                  {busy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending your code…</>)
                        : (<>Send sign-in code <ArrowRight className="w-4 h-4 ml-2" /></>)}
                </Button>
                <p className="text-xs text-muted-foreground">
                  Not staff? <Link to="/portal/login" className="underline underline-offset-4">Customer sign in</Link>
                </p>
              </div>
            </form>
          ) : (
            <form onSubmit={submitCode} data-testid="staff-login-form-code">
              <button
                type="button"
                onClick={() => { setStep("email"); setCode(""); setPendingId(""); setResendIn(0); }}
                className="inline-flex items-center text-xs text-muted-foreground hover:text-foreground mt-2"
                data-testid="staff-login-back-to-email"
              >
                <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Change email
              </button>
              <h1 className="text-2xl font-semibold mt-3">Check your inbox</h1>
              <p className="text-sm text-muted-foreground mt-1">
                We sent a 6-digit code to <span className="font-medium text-foreground">{trimmedEmail}</span>. It's valid for 15 minutes.
              </p>
              <div className="mt-8 space-y-5">
                <div>
                  <Label>Sign-in code</Label>
                  <div className="mt-3 flex" data-testid="staff-login-otp-wrapper">
                    <InputOTP
                      maxLength={6}
                      value={code}
                      onChange={(v) => setCode(v.replace(/\D/g, ""))}
                      autoFocus
                      containerClassName="gap-2"
                      data-testid="staff-login-otp"
                    >
                      <InputOTPGroup className="gap-2">
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                          <InputOTPSlot key={i} index={i} className="h-12 w-11 text-lg font-medium rounded-md border first:rounded-md last:rounded-md" data-testid={`staff-login-otp-slot-${i}`} />
                        ))}
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={busy || !codeValid} data-testid="staff-login-submit">
                  {busy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing you in…</>)
                        : (<><KeyRound className="w-4 h-4 mr-2" /> Sign in</>)}
                </Button>
                <button
                  type="button"
                  onClick={resend}
                  disabled={busy || resendIn > 0}
                  className="w-full text-xs text-muted-foreground hover:text-foreground disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5"
                  data-testid="staff-login-resend"
                >
                  <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} />
                  {resendIn > 0
                    ? <span data-testid="staff-login-resend-countdown">Send a new code in {resendIn}s</span>
                    : <span>Didn't receive it? Send a new code</span>}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
      <div className="hidden lg:block lg:col-span-3 relative">
        <img alt="Spa" className="absolute inset-0 w-full h-full object-cover"
          src="https://images.unsplash.com/photo-1761470575018-135c213340eb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBzcGElMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODUxMjg2NTF8MA&ixlib=rb-4.1.0&q=85" />
      </div>
    </div>
  );
}
